import subscriptionApi from "@/api/subscription.api";
import { notificationError } from "@/common/helpers/notifications.helper";
import { i18n } from "@/plugins/i18n";
import { getPricingExperimentVariant } from "@/services/PostHogService";

/**
 * Persist pricing experiment arm on Stripe checkout create so tools-service can
 * store it (metadata/DB) and attach it to trial_converted_to_paid later.
 * Caller may set posthog_experiment_variant explicitly (preferred when UI arm is known).
 */
const withPosthogExperimentVariant = (payload = {}) => {
  if (Object.prototype.hasOwnProperty.call(payload, "posthog_experiment_variant")) {
    return payload;
  }

  return {
    ...payload,
    posthog_experiment_variant: getPricingExperimentVariant(),
  };
};

export const getAccountSubscriptions = async (id) => {
  return await subscriptionApi.list(id);
};

export const getStationSubscriptions = async (id) => {
  return await subscriptionApi.list(id, "stations");
};

export const getStationSubscription = async (id, priceId) => {
  return await subscriptionApi.get(id, priceId, "stations");
};

export const cancelStationSubscription = async (id, priceId) => {
  return await subscriptionApi.cancel(id, priceId, "stations");
};

export const cancelAccountSubscription = async (id, priceId) => {
  return await subscriptionApi.cancel(id, priceId);
};

export const createAccountSubscription = async (id, data) => {
  return await subscriptionApi.create(id, withPosthogExperimentVariant(data));
};

export const getAccountPlans = async (id, useCurrencyOptions = false) => {
  return await subscriptionApi.getAccountPlans(id, useCurrencyOptions);
};

export const getAccountPlanFeatures = async (id) => {
  return await subscriptionApi.getAccountPlansFeatures(id);
};

export const checkCanUpdateSubscription = async (accountId, priceId, quantity, promoCode) => {
  const result = await subscriptionApi.canUpdateSubscription(
    accountId,
    priceId,
    quantity,
    promoCode
  );

  if (!result.updateAllowed) {
    let message = "Unknown error occurred";

    if (result.reason?.stationsCountExceeded) {
      message = i18n.t("subscriptions.stations_count_exceeded");
    } else if (result.reason?.podcastsCountExceeded) {
      message = i18n.t("subscriptions.podcasts_count_exceeded");
    } else if (result.reason?.maxTracksExceeded) {
      message = i18n.t("subscriptions.max_tracks_exceeded");
    } else if (result.reason?.maxBitRateExceeded) {
      message = i18n.t("subscriptions.max_bit_rate_exceeded");
    } else if (result.reason?.invalidQuantity) {
      message = i18n.t("subscriptions.invalid_quantity");
    } else if (result.reason?.invalidPromoCode) {
      message = i18n.t("subscriptions.invalid_promo_code");
    } else if (result.reason?.noPaymentMethod) {
      message = i18n.t("subscriptions.no_payment_method");
    }

    await notificationError(
      message,
      i18n.t("subscriptions.plan_update_error"),
      12000
    );
  }

  return result.updateAllowed;
};

export const addAccountSubscriptionPromoCode = async (accountId, subscriptionId, promoCode) => {
  return await subscriptionApi.addAccountSubscriptionPromoCode(
    accountId,
    subscriptionId,
    promoCode
  );
};

export const createAccountSubscriptionMultiItem = async (accountId, payload) => {
  return await subscriptionApi.createAccountSubscriptionMultiItem(
    accountId,
    withPosthogExperimentVariant(payload)
  );
};

export const getCheckoutSessionStatus = async (accountId, sessionId) => {
  return await subscriptionApi.getCheckoutSessionStatus(accountId, sessionId);
};

export const previewAccountSubscriptionUpdate = async (accountId, params) => {
  return await subscriptionApi.previewSubscriptionUpdate(accountId, params);
};

export const updateAccountSubscriptionMultiItem = async (accountId, payload) => {
  return await subscriptionApi.updateAccountSubscriptionMultiItem(accountId, payload);
};

export default {
  getAccountSubscriptions,
  getStationSubscriptions,
  getStationSubscription,
  cancelStationSubscription,
  cancelAccountSubscription,
  createAccountSubscription,
  getAccountPlans,
  getAccountPlanFeatures,
  checkCanUpdateSubscription,
  addAccountSubscriptionPromoCode,
  createAccountSubscriptionMultiItem,
  getCheckoutSessionStatus,
  previewAccountSubscriptionUpdate,
  updateAccountSubscriptionMultiItem,
};
